import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from "react";
import { Link as RouterLink, type LinkProps as RouterLinkProps } from "react-router";
import { cn } from "@/shared/lib/cn";

type LinkVariant = "default" | "muted" | "button" | "buttonSecondary";

interface BaseLinkProps {
  variant?: LinkVariant;
  children: ReactNode;
  className?: string;
}

interface ExternalLinkProps
  extends BaseLinkProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children" | "className"> {
  /** External URL (http://, https://, mailto:, etc.) — renders as a span if empty */
  href: string | undefined;
  /** Internal path — do NOT use for external links */
  to?: never;
  /** Open in a new tab (default: true for external links) */
  external?: boolean;
}

interface InternalLinkProps extends BaseLinkProps, Omit<RouterLinkProps, "children" | "className"> {
  /** External URL — do NOT use for internal links */
  href?: never;
  /** Internal path (e.g. /login, /register) */
  to: string;
  external?: never;
}

export type LinkProps = ExternalLinkProps | InternalLinkProps;

/**
 * Unified link component for internal and external navigation.
 *
 * @example
 * ```tsx
 * // Internal link (React Router)
 * <Link to="/login">Sign in</Link>
 *
 * // External link (opens in new tab)
 * <Link href="https://example.com">External site</Link>
 *
 * // External link, same tab
 * <Link href="https://example.com" external={false}>External site</Link>
 *
 * // Muted style
 * <Link to="/home" variant="muted">Back</Link>
 *
 * // Button-styled link (semantically <a>, visually a button)
 * <Link to="/login" variant="button">Sign in</Link>
 * <Link to="/register" variant="buttonSecondary">Register</Link>
 * ```
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ variant = "default", className, children, ...props }, ref) => {
    const isButtonVariant = variant === "button" || variant === "buttonSecondary";

    const linkClasses = cn(
      "cursor-pointer transition-[color,background-color,border-color]",
      variant === "default" && "text-brand-primary font-medium hover:underline",
      variant === "muted" && "text-gray-600 hover:text-gray-800",
      isButtonVariant && [
        "control-md inline-flex items-center justify-center gap-2 rounded-lg font-semibold no-underline",
        "focus-visible:ring-brand-primary focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
      ],
      variant === "button" && [
        "bg-gradient-brand text-white shadow-sm",
        "hover:opacity-90 hover:no-underline",
      ],
      variant === "buttonSecondary" && [
        "border-brand-primary text-brand-primary border-2 bg-white",
        "hover:bg-brand-primary hover:text-white hover:no-underline",
        "disabled:hover:bg-white disabled:hover:text-brand-primary",
      ],
      className
    );

    // Internal link (React Router)
    if ("to" in props && props.to) {
      const { to, ...rest } = props;
      return (
        <RouterLink ref={ref} className={linkClasses} to={to} {...rest}>
          {children}
        </RouterLink>
      );
    }

    // External link
    if ("href" in props && props.href) {
      const { href, external = true, ...rest } = props;
      return (
        <a
          ref={ref}
          className={linkClasses}
          href={href}
          {...(external && {
            target: "_blank",
            rel: "noopener noreferrer",
          })}
          {...rest}
        >
          {children}
        </a>
      );
    }

    // Fallback — should never be reached
    return (
      <span ref={ref} className={linkClasses}>
        {children}
      </span>
    );
  }
);

Link.displayName = "Link";
